import React, { useState, useRef, useEffect } from 'react';

/**
 * Searchable/typeable select dropdown component (combobox)
 * Allows selecting from a list or typing a custom value
 * @param {Object} props
 * @param {string} props.value - Current selected value
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Array} props.options - Array of options { id, name, value? }
 * @param {string} props.placeholder - Placeholder text
 * @param {boolean} props.hasError - Whether to show error styling
 * @param {string} props.errorMessage - Error message to display
 * @param {string} props.label - Label text
 * @param {boolean} props.required - Whether field is required
 * @param {boolean} props.disabled - Whether dropdown is disabled
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = 'Type or select an option',
  hasError = false,
  errorMessage = '',
  label = '',
  required = false,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState(value || '');
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Update input value when value prop changes (e.g., when loading existing data)
  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
        // If input has a value that's not in options, keep it
        if (inputValue && !options.find(opt => (opt.value || opt.name || opt.id) === inputValue)) {
          onChange(inputValue);
        }
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, inputValue, options, onChange]);

  // Filter options based on search term
  const filteredOptions = options.filter((option) => {
    const optionText = (option.name || option.value || option.id || '').toLowerCase();
    return optionText.includes(searchTerm.toLowerCase());
  });

  // Check if current value matches an option
  const selectedOption = options.find(
    (opt) => (opt.value || opt.name || opt.id) === value
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    setIsOpen(true);
    // Update parent immediately as user types
    onChange(newValue);
  };

  const handleSelect = (option) => {
    const optionValue = option.value || option.name || option.id;
    setInputValue(optionValue);
    setSearchTerm('');
    onChange(optionValue);
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
    setSearchTerm(inputValue || '');
  };

  const handleInputBlur = (e) => {
    // Delay closing to allow option click
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    }, 200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredOptions.length > 0 && searchTerm) {
      // Select first filtered option on Enter
      handleSelect(filteredOptions[0]);
      e.preventDefault();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  return (
    <div style={{ marginBottom: '2.5rem' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: '1rem',
            fontWeight: 500,
            color: '#374151',
            marginBottom: '0.75rem',
          }}
        >
          {label} {required && <span style={{ color: '#ef4444' }}>*</span>}
        </label>
      )}

      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <input
            ref={inputRef}
            type="text"
            disabled={disabled}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={(e) => {
              handleInputFocus();
              if (!disabled && !hasError) {
                e.target.style.borderColor = '#2563eb';
              }
            }}
            onBlur={(e) => {
              handleInputBlur(e);
              if (!hasError) {
                e.target.style.borderColor = '#d1d5db';
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            style={{
              width: '100%',
              padding: '1rem 2.5rem 1rem 1rem',
              fontSize: '1rem',
              border: hasError ? '1px solid #ef4444' : '1px solid #d1d5db',
              borderRadius: '0.5rem',
              outline: 'none',
              backgroundColor: disabled ? '#f9fafb' : '#ffffff',
              color: inputValue ? '#111827' : '#9ca3af',
              cursor: disabled ? 'not-allowed' : 'text',
              transition: 'border-color 0.2s',
            }}
          />
          <button
            type="button"
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
                if (!isOpen) {
                  setSearchTerm(inputValue || '');
                  setTimeout(() => inputRef.current?.focus(), 0);
                }
              }
            }}
            style={{
              position: 'absolute',
              right: '0.5rem',
              background: 'none',
              border: 'none',
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '0.25rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6b7280',
            }}
            onMouseEnter={(e) => {
              if (!disabled) {
                e.target.style.color = '#374151';
              }
            }}
            onMouseLeave={(e) => {
              if (!disabled) {
                e.target.style.color = '#6b7280';
              }
            }}
          >
            <svg
              style={{
                width: '1.25rem',
                height: '1.25rem',
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>

        {isOpen && !disabled && (
          <div
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '0.25rem',
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '0.5rem',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
              zIndex: 1000,
              maxHeight: '16rem',
              overflowY: 'auto',
            }}
          >
            {filteredOptions.length === 0 && !searchTerm ? (
              <div
                style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                }}
              >
                No options available
              </div>
            ) : filteredOptions.length === 0 && searchTerm ? (
              <div
                style={{
                  padding: '0.75rem',
                  textAlign: 'center',
                  color: '#6b7280',
                  fontSize: '0.875rem',
                }}
              >
                No matches. Press Enter to use "{searchTerm}"
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const optionValue = option.value || option.name || option.id;
                const isSelected = value === optionValue;

                return (
                  <button
                    key={option.id || index}
                    type="button"
                    onClick={() => handleSelect(option)}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      textAlign: 'left',
                      fontSize: '1rem',
                      color: isSelected ? '#2563eb' : '#111827',
                      backgroundColor: isSelected ? '#eff6ff' : 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    onMouseEnter={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = '#f9fafb';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSelected) {
                        e.target.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>{option.name || option.value || option.id}</span>
                    {isSelected && (
                      <svg
                        style={{
                          width: '1rem',
                          height: '1rem',
                          color: '#2563eb',
                          flexShrink: 0,
                        }}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        )}
      </div>

      {hasError && errorMessage && (
        <p
          style={{
            marginTop: '0.5rem',
            fontSize: '0.875rem',
            color: '#ef4444',
          }}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

